/**
 * จ่ายนอกรอบเงินเดือน (ค่าคอม/OT) — Owner/HR.
 *
 * ลูกค้าคำนวณค่าคอมเองใน Excel แล้วโอนแยกจากรอบเงินเดือน — หน้านี้คือสมุดบันทึก:
 * สร้างรอบ → ใส่ยอดรายคนแยกประเภท (กรอกมือ / import ไฟล์) → ระบบคิด WHT/สุทธิ →
 * ยืนยัน "จ่ายแล้ว" = ล็อก + พนักงานเห็นสลิปใน LINE (/my-payouts).
 * ไม่เกี่ยวกับรอบเงินเดือนปกติ (payroll run) เลย.
 */
import { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  usePayoutTypes, useUpsertPayoutType,
  usePayoutRounds, useUpsertPayoutRound, usePayoutRound,
  useSavePayoutItems, useMarkPayoutPaid, useCancelPayoutRound, usePayoutReport,
  useReleasePayslipPeriod, useEmployees,
  type PayoutType, type PayoutRoundSummary, type PayoutRoundDetail,
} from '../../hooks';
import { apiDownload, apiFetch } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { Modal } from '../../components/ui/Modal';
import { EmployeeMultiPicker, type PickerEmployee } from '../../components/ui/EmployeePicker';
import { LoadError } from '../../components/layout/LoadError';
import { todayISO } from '../../lib/thai-date';
import {
  Plus, ChevronLeft, Save, CheckCircle2, Download, Landmark, Upload,
  Trash2, X, FileSpreadsheet, Lock, Ban, Pencil,
} from 'lucide-react';
import { openWhenReady } from '../../lib/open-when-ready';

const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const thaiDate = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return `${d} ${THAI_MONTHS_SHORT[(m ?? 1) - 1]} ${(y ?? 0) + 543}`;
};

const STATUS_BADGE: Record<PayoutRoundSummary['status'], { label: string; cls: string }> = {
  draft: { label: 'ร่าง', cls: 'badge-warn' },
  paid: { label: 'จ่ายแล้ว', cls: 'badge-success' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};

interface EmpRow {
  emp_code: string;
  first_name: string;
  last_name: string;
  nickname?: string | null;
  department?: string | null;
  position?: string | null;
  status?: string | null;
}

/** แถวในตารางแก้ไข — เก็บเป็น string ระหว่างพิมพ์ กัน NaN */
interface EditRow {
  emp_code: string;
  name: string;
  dept: string;
  values: Record<string, string>;  // type_code → amount (string)
  wht: string;                     // percent
}

export function HRPayouts() {
  const [view, setView] = useState<'rounds' | 'report' | 'types'>('rounds');
  const [roundId, setRoundId] = useState<string | null>(null);

  return (
    <AdminLayout title="จ่ายนอกรอบ (ค่าคอม/OT)">
      {roundId ? (
        <RoundDetail id={roundId} onBack={() => setRoundId(null)} />
      ) : (
        <>
          <div className="flex gap-1 mb-3 border-b border-gray-200">
            {([['rounds', 'รอบจ่าย'], ['report', 'สรุปทั้งปี'], ['types', 'ประเภทรายการ']] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`px-3 py-2 text-sm font-medium border-b-2 ${view === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {view === 'rounds' ? <RoundList onOpen={setRoundId} /> : view === 'report' ? <YearReport /> : <TypesTab />}
        </>
      )}
    </AdminLayout>
  );
}

// === รายการรอบจ่าย ===
function RoundList({ onOpen }: { onOpen: (id: string) => void }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const rounds = usePayoutRounds(year);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        บันทึกการจ่ายค่าคอม/OT ที่โอนแยกจากรอบเงินเดือน — ใส่ยอดที่คำนวณไว้แล้ว ระบบเก็บประวัติ คิดหัก ณ ที่จ่าย และออกสลิปให้พนักงาน
      </p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input w-auto">
          {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>ปี {y + 543}</option>
          ))}
        </select>
        <button onClick={() => setCreating(true)} className="btn-cta inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> สร้างรอบจ่าย
        </button>
      </div>

      {rounds.isLoading ? (
        <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>
      ) : (rounds.data ?? []).length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">ยังไม่มีรอบจ่ายในปีนี้ — กด "สร้างรอบจ่าย" เพื่อเริ่ม</p>
      ) : (
        <div className="space-y-2">
          {(rounds.data ?? []).map((r) => (
            <button key={r.id} onClick={() => onOpen(r.id)} className="card p-3 w-full text-left hover:bg-gray-50 transition">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                    {r.status === 'paid' && <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                    {r.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    จ่ายวันที่ {thaiDate(r.pay_date)} · {r.totals.people} คน
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={STATUS_BADGE[r.status].cls}>{STATUS_BADGE[r.status].label}</span>
                  <div className="text-sm font-semibold text-gray-900 mt-1">฿{fmt(r.totals.net)}</div>
                  {((r.totals.deduct ?? 0) > 0 || r.totals.wht > 0) && (
                    <div className="text-[11px] text-gray-400">
                      หัก ฿{fmt((r.totals.deduct ?? 0) + r.totals.wht)}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <RoundMetaModal onClose={() => setCreating(false)} onCreated={onOpen} />}
    </div>
  );
}

// === modal สร้าง/แก้ข้อมูลรอบ ===
function RoundMetaModal({ initial, onClose, onCreated }: {
  initial?: { id: string; title: string; pay_date: string; wht_default_percent: number; note: string | null };
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const upsert = useUpsertPayoutRound();
  const toast = useToast();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [payDate, setPayDate] = useState(initial?.pay_date ?? todayISO());
  const [wht, setWht] = useState(String(initial?.wht_default_percent ?? 3));
  const [note, setNote] = useState(initial?.note ?? '');

  function save() {
    if (!title.trim()) { toast.error('ตั้งชื่อรอบก่อน เช่น "ค่าคอม+OT พ.ค. 69"'); return; }
    upsert.mutate(
      { id: initial?.id, title: title.trim(), pay_date: payDate, wht_default_percent: Number(wht) || 0, note: note || undefined },
      {
        onSuccess: (r) => { toast.success('บันทึกรอบแล้ว'); onClose(); if (!initial) onCreated?.(r.id); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <Modal open onClose={onClose} title={initial ? 'แก้ข้อมูลรอบ' : 'สร้างรอบจ่าย'}>
      <div className="space-y-3">
        <div>
          <label className="label">ชื่อรอบ *</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder='เช่น "ค่าคอม+OT พ.ค. 69"' />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">วันที่จ่าย/โอน *</label>
            <input type="date" className="input" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </div>
          <div>
            <label className="label">หัก ณ ที่จ่าย (%) ค่าตั้งต้น</label>
            <input type="number" step="0.01" min="0" className="input" value={wht} onChange={(e) => setWht(e.target.value)} />
            <p className="text-[11px] text-gray-400 mt-0.5">ปรับรายคนได้ในตาราง (ใส่ 0 = ไม่หัก)</p>
          </div>
        </div>
        <div>
          <label className="label">หมายเหตุ</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <button onClick={save} disabled={upsert.isPending} className="btn-cta w-full justify-center flex items-center gap-2">
          <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}

// === รายละเอียดรอบ + ตารางแก้ไข ===
function RoundDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const detail = usePayoutRound(id);
  const types = usePayoutTypes();
  const { data: employees } = useEmployees() as { data: EmpRow[] | undefined };
  const saveItems = useSavePayoutItems();
  const markPaid = useMarkPayoutPaid();
  const cancelRound = useCancelPayoutRound();
  const releaseSlip = useReleasePayslipPeriod();
  const toast = useToast();
  const confirm = useConfirm();

  const [rows, setRows] = useState<EditRow[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);

  const round = detail.data?.round;
  const isDraft = round?.status === 'draft';
  const isPaid = round?.status === 'paid';
  const [slipLoading, setSlipLoading] = useState<string | null>(null);
  async function downloadSlip(empCode: string) {
    if (slipLoading) return;
    setSlipLoading(empCode);
    try {
      await openWhenReady(async () => (await apiFetch<{ pdf_url: string }>(`/hr/payouts/rounds/${id}/slip-pdf?emp=${encodeURIComponent(empCode)}`)).pdf_url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างสลิปไม่สำเร็จ');
    } finally {
      setSlipLoading(null);
    }
  }
  const activeTypes = useMemo(() => (types.data ?? []).filter((t) => t.active), [types.data]);
  const deductCodes = useMemo(() => new Set(activeTypes.filter((t) => t.kind === 'deduction').map((t) => t.code)), [activeTypes]);
  const hasDeduct = deductCodes.size > 0;
  const empByCode = useMemo(() => new Map((employees ?? []).map((e) => [e.emp_code, e])), [employees]);

  // โหลดรายการจาก server → state แก้ไข (รีเซ็ตเมื่อเปลี่ยนรอบ/refetch หลัง save)
  useEffect(() => {
    if (!detail.data) return;
    setRows(detail.data.items.map((it) => ({
      emp_code: it.emp_code,
      name: it.employee ? `${it.employee.first_name} ${it.employee.last_name}`.trim() + (it.employee.nickname ? ` (${it.employee.nickname})` : '') : it.emp_code,
      dept: it.employee?.department ?? '',
      values: Object.fromEntries(Object.entries(it.breakdown).map(([k, v]) => [k, String(v)])),
      wht: String(it.wht_percent),
    })));
    setRemoved(new Set());
    setDirty(false);
  }, [detail.data]);

  // สุทธิ = เงินเพิ่ม - เงินหัก - WHT (WHT คิดจากยอดหลังหัก ติดลบไม่คิด — ตรงกับ worker)
  const calc = (r: EditRow) => {
    let earn = 0; let deduct = 0;
    for (const [k, v] of Object.entries(r.values)) {
      const n = Number(v) || 0;
      if (deductCodes.has(k)) deduct += n; else earn += n;
    }
    const base = earn - deduct;
    const whtPct = Math.min(100, Math.max(0, Number(r.wht) || 0));
    const whtAmt = base > 0 ? base * whtPct / 100 : 0;
    return { earn, deduct, whtAmt, net: base - whtAmt };
  };
  const totals = rows.reduce(
    (t, r) => { const c = calc(r); return { earn: t.earn + c.earn, deduct: t.deduct + c.deduct, wht: t.wht + c.whtAmt, net: t.net + c.net }; },
    { earn: 0, deduct: 0, wht: 0, net: 0 },
  );

  function setValue(emp: string, type: string, v: string) {
    setRows((rs) => rs.map((r) => (r.emp_code === emp ? { ...r, values: { ...r.values, [type]: v } } : r)));
    setDirty(true);
  }
  function setWhtRow(emp: string, v: string) {
    setRows((rs) => rs.map((r) => (r.emp_code === emp ? { ...r, wht: v } : r)));
    setDirty(true);
  }
  function removeRow(emp: string) {
    setRows((rs) => rs.filter((r) => r.emp_code !== emp));
    setRemoved((s) => new Set(s).add(emp));
    setDirty(true);
  }
  function addEmployees(codes: string[]) {
    const defaultWht = String(round?.wht_default_percent ?? 3);
    setRows((rs) => {
      const have = new Set(rs.map((r) => r.emp_code));
      const added = codes.filter((c) => !have.has(c)).map((c) => {
        const e = empByCode.get(c);
        return {
          emp_code: c,
          name: e ? `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '') : c,
          dept: e?.department ?? '',
          values: {},
          wht: defaultWht,
        };
      });
      return [...rs, ...added];
    });
    setRemoved((s) => { const next = new Set(s); codes.forEach((c) => next.delete(c)); return next; });
    setDirty(true);
  }

  async function handleSave(): Promise<boolean> {
    try {
      await saveItems.mutateAsync({
        round_id: id,
        items: rows.map((r) => ({
          emp_code: r.emp_code,
          breakdown: Object.fromEntries(
            Object.entries(r.values).map(([k, v]) => [k, Number(v) || 0]).filter(([, v]) => v !== 0),
          ) as Record<string, number>,
          wht_percent: Number(r.wht) || 0,
        })),
        remove_emp_codes: [...removed],
      });
      toast.success('บันทึกแล้ว');
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
      return false;
    }
  }

  async function handleMarkPaid() {
    if (dirty && !(await handleSave())) return;
    const ok = await confirm({
      title: 'ยืนยันว่าจ่ายรอบนี้แล้ว?',
      message: `${rows.length} คน · ยอดสุทธิ ฿${fmt(totals.net)} (${totals.deduct > 0 ? `รายการหัก ฿${fmt(totals.deduct)} · ` : ''}หัก ณ ที่จ่าย ฿${fmt(totals.wht)}) — ยืนยันแล้วรอบจะถูกล็อก แก้ไขไม่ได้ และพนักงานจะเห็นสลิปใน LINE`,
      confirmText: 'ยืนยันจ่ายแล้ว',
    });
    if (!ok) return;
    markPaid.mutate(id, {
      onSuccess: () => toast.success('บันทึกว่าจ่ายแล้ว — รอบถูกล็อก'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: 'ยกเลิกรอบนี้?',
      message: round?.status === 'paid'
        ? 'รอบนี้บันทึกว่าจ่ายแล้ว — การยกเลิกจะลบออกจากสลิปพนักงานและรายงาน (ข้อมูลยังเก็บไว้ตรวจย้อนได้)'
        : 'ร่างรอบนี้จะถูกยกเลิก',
      confirmText: 'ยกเลิกรอบ', danger: true,
    });
    if (!ok) return;
    cancelRound.mutate(id, {
      onSuccess: () => { toast.success('ยกเลิกรอบแล้ว'); onBack(); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  async function handleReleaseSlip() {
    if (!round?.period) { toast.error('รอบนี้ยังไม่มีงวด (period)'); return; }
    const ok = await confirm({
      title: 'อนุมัติออกสลิปงวดนี้?',
      message: 'พนักงานจะโหลดสลิปเงินเดือนงวดนี้ได้ทันที (โดยไม่ต้องรอจ่ายค่าคอม) — ใช้กับงวดที่ไม่มีค่าคอม',
      confirmText: 'อนุมัติออกสลิป',
    });
    if (!ok) return;
    releaseSlip.mutate(round.period, {
      onSuccess: () => toast.success('อนุมัติออกสลิปงวดนี้แล้ว — พนักงานโหลดสลิปได้'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  // === import ไฟล์ (xlsx/csv): คอลัมน์แรก = รหัสพนักงาน, หัวคอลัมน์ที่เหลือจับคู่ชื่อ/โค้ดประเภท ===
  async function handleImport(file: File) {
    try {
      const XLSX = await import('xlsx');
      const wb = file.name.endsWith('.csv')
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]!]!;
      const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' });
      if (aoa.length < 2) { toast.error('ไฟล์ว่าง — ต้องมีหัวตาราง + ข้อมูลอย่างน้อย 1 แถว'); return; }

      const header = aoa[0]!.map((h) => String(h).trim());
      const typeByHeader = new Map<number, string>();
      let codeCol = -1;
      header.forEach((h, idx) => {
        const lower = h.toLowerCase();
        if (codeCol < 0 && (lower === 'emp_code' || h === 'รหัส' || h === 'รหัสพนักงาน')) { codeCol = idx; return; }
        // หมายเหตุ: ไม่รับคอลัมน์ "หัก ณ ที่จ่าย/wht" จากไฟล์ — ค่านี้เป็นสตางค์ในไฟล์ลูกค้า
        // แต่ในระบบเป็น % ถ้า map ตรงๆ ยอดจะเพี้ยนมหาศาล (เช่น 197.20 บาท → 197.20%)
        // ใช้ default ของรอบเสมอ แล้วให้ HR แก้รายคนในตารางแทน
        const t = activeTypes.find((x) => x.label === h || x.code === lower);
        if (t) typeByHeader.set(idx, t.code);
      });
      if (codeCol < 0) { toast.error('ไม่พบคอลัมน์ "รหัสพนักงาน" (emp_code) — ใช้เทมเพลตที่ดาวน์โหลดจากหน้านี้'); return; }
      if (typeByHeader.size === 0) { toast.error('ไม่พบคอลัมน์ประเภทรายการที่ตรงกับระบบ — หัวคอลัมน์ต้องตรงกับชื่อประเภท'); return; }

      const defaultWht = String(round?.wht_default_percent ?? 3);
      let matched = 0; const unknown: string[] = [];
      const imported = new Map<string, EditRow>();
      for (const raw of aoa.slice(1)) {
        const code = String(raw[codeCol] ?? '').trim();
        if (!code) continue;
        if (!empByCode.has(code)) { unknown.push(code); continue; }
        const e = empByCode.get(code)!;
        const values: Record<string, string> = {};
        for (const [idx, typeCode] of typeByHeader) {
          const n = Number(String(raw[idx] ?? '').replace(/,/g, ''));
          if (Number.isFinite(n) && n !== 0) values[typeCode] = String(n);
        }
        if (Object.keys(values).length === 0) continue;
        imported.set(code, {
          emp_code: code,
          name: `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : ''),
          dept: e.department ?? '',
          values,
          wht: defaultWht,
        });
        matched++;
      }
      if (matched === 0) { toast.error('ไม่มีแถวที่นำเข้าได้ — เช็ครหัสพนักงานและยอดในไฟล์'); return; }
      // ทับแถวเดิมที่รหัสซ้ำ + เพิ่มแถวใหม่
      setRows((rs) => [...rs.filter((r) => !imported.has(r.emp_code)), ...imported.values()]
        .sort((a, b) => a.emp_code.localeCompare(b.emp_code)));
      setRemoved((s) => { const next = new Set(s); imported.forEach((_, c) => next.delete(c)); return next; });
      setDirty(true);
      toast.success(`นำเข้า ${matched} คน${unknown.length ? ` · ข้ามรหัสที่ไม่พบ ${unknown.length} รายการ (${unknown.slice(0, 5).join(', ')}${unknown.length > 5 ? '…' : ''})` : ''}`);
    } catch (e) {
      toast.error(`อ่านไฟล์ไม่สำเร็จ: ${e instanceof Error ? e.message : 'รูปแบบไฟล์ไม่รองรับ'}`);
    }
  }

  // เทมเพลต CSV: พนักงาน active ทุกคน + คอลัมน์ประเภท (ลบแถวที่ไม่จ่ายออกได้)
  function downloadTemplate() {
    const actives = (employees ?? []).filter((e) => e.status === 'active' || e.status === 'probation');
    const header = ['emp_code', 'ชื่อ-สกุล', ...activeTypes.map((t) => t.label)];
    const lines = [header, ...actives.map((e) => [e.emp_code, `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : ''), ...activeTypes.map(() => '')])];
    const csv = '﻿' + lines.map((row) => row.map((c) => (/[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c)).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'payout-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (detail.isLoading) return <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>;
  if (detail.error || !round) return <LoadError error={detail.error} onRetry={() => detail.refetch()} />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} className="btn-secondary btn-slim inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> รอบจ่ายทั้งหมด
        </button>
        <span className={STATUS_BADGE[round.status].cls}>{STATUS_BADGE[round.status].label}</span>
      </div>

      <div className="card p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold text-gray-900 flex items-center gap-2">
              {round.status === 'paid' && <Lock className="w-4 h-4 text-gray-400" />}
              {round.title}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              จ่ายวันที่ {thaiDate(round.pay_date)} · หัก ณ ที่จ่ายตั้งต้น {Number(round.wht_default_percent)}%
              {round.note && <> · {round.note}</>}
            </div>
          </div>
          {isDraft && (
            <button onClick={() => setEditingMeta(true)} className="p-1.5 rounded hover:bg-gray-100 shrink-0">
              <Pencil className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* แถบเครื่องมือ */}
      <div className="flex items-center gap-2 flex-wrap">
        {isDraft && (
          <>
            <button onClick={() => setAdding(true)} className="btn-secondary btn-slim inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> เพิ่มพนักงาน
            </button>
            <label className="btn-secondary btn-slim inline-flex items-center gap-1 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> นำเข้าไฟล์
              <input
                type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImport(f); e.target.value = ''; }}
              />
            </label>
            <button onClick={downloadTemplate} className="btn-secondary btn-slim inline-flex items-center gap-1">
              <FileSpreadsheet className="w-3.5 h-3.5" /> เทมเพลต
            </button>
          </>
        )}
        <span className="flex-1" />
        <button onClick={() => void apiDownload(`/hr/payouts/rounds/${id}/export`, `payout-${round.pay_date}.csv`)} className="btn-secondary btn-slim inline-flex items-center gap-1">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
        <button onClick={() => void apiDownload(`/hr/payouts/rounds/${id}/bank-transfer`, `payout-bank-${round.pay_date}.csv`)} className="btn-secondary btn-slim inline-flex items-center gap-1">
          <Landmark className="w-3.5 h-3.5" /> ไฟล์โอนธนาคาร
        </button>
      </div>

      {/* ตาราง */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
              <th className="px-3 py-2 font-medium">พนักงาน</th>
              {activeTypes.map((t) => (
                <th key={t.code} className={`px-2 py-2 font-medium text-right whitespace-nowrap ${t.kind === 'deduction' ? 'text-red-500' : ''}`}>
                  {t.label}
                </th>
              ))}
              <th className="px-2 py-2 font-medium text-right whitespace-nowrap">{hasDeduct ? 'รวมเพิ่ม' : 'รวม'}</th>
              {hasDeduct && <th className="px-2 py-2 font-medium text-right text-red-500 whitespace-nowrap">รวมหัก</th>}
              <th className="px-2 py-2 font-medium text-right whitespace-nowrap">หัก ณ ที่จ่าย %</th>
              <th className="px-2 py-2 font-medium text-right">สุทธิ</th>
              {(isDraft || isPaid) && <th className="px-2 py-2 w-8" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={activeTypes.length + (hasDeduct ? 5 : 4)} className="px-3 py-8 text-center text-gray-400">
                ยังไม่มีรายการ — กด "เพิ่มพนักงาน" หรือ "นำเข้าไฟล์"
              </td></tr>
            )}
            {rows.map((r) => {
              const c = calc(r);
              return (
                <tr key={r.emp_code} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5">
                    <div className="text-gray-900 whitespace-nowrap">{r.name}</div>
                    <div className="text-[11px] text-gray-400">{r.dept || '—'} · <span className="font-mono">{r.emp_code}</span></div>
                  </td>
                  {activeTypes.map((t) => (
                    <td key={t.code} className="px-2 py-1.5 text-right">
                      {isDraft ? (
                        <input
                          type="number" step="0.01" inputMode="decimal"
                          value={r.values[t.code] ?? ''}
                          onChange={(e) => setValue(r.emp_code, t.code, e.target.value)}
                          className={`w-24 rounded-lg border px-2 py-1 text-xs text-right focus:outline-none ${t.kind === 'deduction' ? 'border-red-200 text-red-600 focus:border-red-400' : 'border-gray-200 focus:border-brand-400'}`}
                        />
                      ) : (
                        <span className={t.kind === 'deduction' ? 'text-red-600' : 'text-gray-700'}>
                          {r.values[t.code] ? (t.kind === 'deduction' ? `-${fmt(Number(r.values[t.code]))}` : fmt(Number(r.values[t.code]))) : ''}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right font-medium text-gray-900 whitespace-nowrap">{fmt(c.earn)}</td>
                  {hasDeduct && (
                    <td className="px-2 py-1.5 text-right font-medium text-red-600 whitespace-nowrap">{c.deduct > 0 ? `-${fmt(c.deduct)}` : ''}</td>
                  )}
                  <td className="px-2 py-1.5 text-right">
                    {isDraft ? (
                      <input
                        type="number" step="0.01" min="0" max="100" inputMode="decimal"
                        value={r.wht}
                        onChange={(e) => setWhtRow(r.emp_code, e.target.value)}
                        className="w-14 rounded-lg border border-gray-200 px-1.5 py-1 text-xs text-right focus:border-brand-400 focus:outline-none"
                      />
                    ) : (
                      <span className="text-gray-500">{Number(r.wht)}%</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmt(c.net)}</td>
                  {isDraft && (
                    <td className="px-2 py-1.5">
                      <button onClick={() => removeRow(r.emp_code)} className="p-1 text-gray-300 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                  {isPaid && (
                    <td className="px-2 py-1.5">
                      <button
                        onClick={() => void downloadSlip(r.emp_code)}
                        disabled={slipLoading === r.emp_code}
                        className="p-1 text-gray-400 hover:text-brand-600 disabled:opacity-40"
                        title="ดาวน์โหลดสลิป PDF"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-200 text-sm font-semibold text-gray-900">
                <td className="px-3 py-2" colSpan={1 + activeTypes.length}>{rows.length} คน</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">{fmt(totals.earn)}</td>
                {hasDeduct && <td className="px-2 py-2 text-right text-red-600 whitespace-nowrap">{totals.deduct > 0 ? `-${fmt(totals.deduct)}` : ''}</td>}
                <td className="px-2 py-2 text-right text-gray-500 font-normal whitespace-nowrap">หัก {fmt(totals.wht)}</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">฿{fmt(totals.net)}</td>
                {(isDraft || isPaid) && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ปุ่มหลัก */}
      {isDraft ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => void handleSave()} disabled={saveItems.isPending || !dirty} className="btn-secondary inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" /> {saveItems.isPending ? 'กำลังบันทึก…' : 'บันทึกร่าง'}
          </button>
          <button onClick={() => void handleMarkPaid()} disabled={markPaid.isPending || rows.length === 0} className="btn-cta inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> ยืนยันจ่ายแล้ว (ล็อกรอบ)
          </button>
          <span className="flex-1" />
          <button onClick={() => void handleCancel()} className="btn-secondary text-red-700 border-red-200 inline-flex items-center gap-1.5">
            <Ban className="w-4 h-4" /> ยกเลิกรอบ
          </button>
        </div>
      ) : round.status === 'paid' ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" /> จ่ายแล้ว {round.paid_at ? `เมื่อ ${new Date(round.paid_at).toLocaleString('th-TH')}` : ''} — แก้ไขไม่ได้ · พนักงานเห็นสลิปใน LINE แล้ว
          </p>
          <div className="flex items-center gap-2">
            {round.round_kind === 'salary' && !round.slip_released && (
              <button onClick={() => void handleReleaseSlip()} className="btn-secondary btn-slim inline-flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> อนุมัติออกสลิปงวดนี้
              </button>
            )}
            <button onClick={() => void handleCancel()} className="btn-secondary btn-slim text-red-700 border-red-200 inline-flex items-center gap-1">
              <Ban className="w-3.5 h-3.5" /> ยกเลิกรอบ (เจ้าของ)
            </button>
          </div>
        </div>
      ) : null}

      {adding && (
        <Modal open onClose={() => setAdding(false)} title="เพิ่มพนักงานเข้ารอบจ่าย" size="max-w-lg">
          <AddEmployeesPanel
            employees={(employees ?? []).filter((e) => e.status === 'active' || e.status === 'probation')}
            existing={new Set(rows.map((r) => r.emp_code))}
            onAdd={(codes) => { addEmployees(codes); setAdding(false); }}
          />
        </Modal>
      )}
      {editingMeta && round && (
        <RoundMetaModal
          initial={{ id: round.id, title: round.title, pay_date: round.pay_date, wht_default_percent: Number(round.wht_default_percent), note: round.note }}
          onClose={() => setEditingMeta(false)}
        />
      )}
    </div>
  );
}

function AddEmployeesPanel({ employees, existing, onAdd }: {
  employees: PickerEmployee[];
  existing: Set<string>;
  onAdd: (codes: string[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const available = employees.filter((e) => !existing.has(e.emp_code));
  return (
    <div className="space-y-3">
      <EmployeeMultiPicker
        employees={available}
        isChecked={(e) => picked.has(e.emp_code)}
        onToggle={(e, on) => setPicked((s) => { const n = new Set(s); if (on) n.add(e.emp_code); else n.delete(e.emp_code); return n; })}
        onToggleAll={(codes, all) => setPicked(all ? new Set() : new Set(codes))}
        selectedCount={picked.size}
        emptyText="พนักงานทุกคนอยู่ในรอบนี้แล้ว"
      />
      <button onClick={() => onAdd([...picked])} disabled={picked.size === 0} className="btn-cta w-full justify-center flex items-center gap-2">
        <Plus className="w-4 h-4" /> เพิ่ม {picked.size || ''} คน
      </button>
    </div>
  );
}

// === สรุปทั้งปี ===
function YearReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const report = usePayoutReport(year);
  const types = usePayoutTypes();

  const usedCodes = useMemo(() => {
    const s = new Set<string>();
    for (const r of report.data?.employees ?? []) for (const k of Object.keys(r.by_type)) s.add(k);
    return s;
  }, [report.data]);
  const cols = (types.data ?? []).filter((t) => usedCodes.has(t.code));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input w-auto">
          {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>ปี {y + 543}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400">นับเฉพาะรอบที่ยืนยันจ่ายแล้ว — ใช้ยอด "หัก ณ ที่จ่าย" ประกอบการยื่นภาษีกับสำนักงานบัญชี</p>
      </div>
      {report.isLoading ? (
        <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>
      ) : (report.data?.employees ?? []).length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">ยังไม่มีรอบที่จ่ายแล้วในปีนี้</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2 font-medium">พนักงาน</th>
                <th className="px-2 py-2 font-medium text-right">กี่รอบ</th>
                {cols.map((t) => (
                  <th key={t.code} className={`px-2 py-2 font-medium text-right whitespace-nowrap ${t.kind === 'deduction' ? 'text-red-500' : ''}`}>
                    {t.label}
                  </th>
                ))}
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">รวมเพิ่ม</th>
                <th className="px-2 py-2 font-medium text-right text-red-500 whitespace-nowrap">รวมหัก</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">หัก ณ ที่จ่าย</th>
                <th className="px-2 py-2 font-medium text-right">สุทธิ</th>
              </tr>
            </thead>
            <tbody>
              {(report.data?.employees ?? []).map((r) => (
                <tr key={r.emp_code} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5">
                    <div className="text-gray-900 whitespace-nowrap">
                      {r.employee ? `${r.employee.first_name} ${r.employee.last_name}`.trim() + (r.employee.nickname ? ` (${r.employee.nickname})` : '') : r.emp_code}
                    </div>
                    <div className="text-[11px] text-gray-400">{r.employee?.department ?? '—'} · <span className="font-mono">{r.emp_code}</span></div>
                  </td>
                  <td className="px-2 py-1.5 text-right text-gray-500">{r.rounds}</td>
                  {cols.map((t) => (
                    <td key={t.code} className={`px-2 py-1.5 text-right whitespace-nowrap ${t.kind === 'deduction' ? 'text-red-600' : 'text-gray-700'}`}>
                      {r.by_type[t.code] ? (t.kind === 'deduction' ? `-${fmt(r.by_type[t.code]!)}` : fmt(r.by_type[t.code]!)) : ''}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right font-medium whitespace-nowrap">{fmt(r.gross)}</td>
                  <td className="px-2 py-1.5 text-right text-red-600 whitespace-nowrap">{(r.deduct ?? 0) > 0 ? `-${fmt(r.deduct)}` : ''}</td>
                  <td className="px-2 py-1.5 text-right text-gray-500 whitespace-nowrap">{fmt(r.wht)}</td>
                  <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">฿{fmt(r.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// === จัดการประเภทรายการ ===
function TypesTab() {
  const types = usePayoutTypes();
  const upsert = useUpsertPayoutType();
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<PayoutType> | null>(null);

  function save() {
    if (!editing?.label?.trim()) { toast.error('ตั้งชื่อประเภทก่อน'); return; }
    const code = editing.code?.trim() || editing.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `type_${Date.now() % 100000}`;
    upsert.mutate(
      { id: editing.id, code, label: editing.label.trim(), kind: editing.kind ?? 'earning', sort_order: editing.sort_order ?? 100, active: editing.active ?? true },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setEditing(null); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">ประเภทรายการ = คอลัมน์ในตารางรอบจ่าย (ตรงกับหัวคอลัมน์ไฟล์ Excel เดิมของบริษัท)</p>
        <button onClick={() => setEditing({})} className="btn-secondary btn-slim inline-flex items-center gap-1 shrink-0">
          <Plus className="w-3.5 h-3.5" /> เพิ่มประเภท
        </button>
      </div>
      <div className="card divide-y divide-gray-50">
        {(types.data ?? []).map((t) => (
          <div key={t.id} className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <span className={`text-sm ${t.active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{t.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ml-2 ${t.kind === 'deduction' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
                {t.kind === 'deduction' ? 'เงินหัก' : 'เงินเพิ่ม'}
              </span>
              <span className="text-[11px] text-gray-400 font-mono ml-2">{t.code}</span>
            </div>
            <button onClick={() => setEditing(t)} className="p-1.5 rounded hover:bg-gray-100">
              <Pencil className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal open onClose={() => setEditing(null)} title={editing.id ? 'แก้ประเภทรายการ' : 'เพิ่มประเภทรายการ'}>
          <div className="space-y-3">
            <div>
              <label className="label">ชื่อที่แสดง *</label>
              <input className="input" value={editing.label ?? ''} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="เช่น ค่าคอมไลฟ์สด" />
            </div>
            <div>
              <label className="label">ชนิดรายการ</label>
              <div className="flex gap-2">
                {([['earning', 'เงินเพิ่ม'], ['deduction', 'เงินหัก']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setEditing({ ...editing, kind: k })}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm ${(editing.kind ?? 'earning') === k
                      ? (k === 'deduction' ? 'border-red-400 bg-red-50 text-red-700 font-medium' : 'border-emerald-400 bg-emerald-50 text-emerald-700 font-medium')
                      : 'border-gray-200 text-gray-500'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">เงินหัก = ระบบเอาไปลบจากยอดจ่าย (กรอกตัวเลขเป็นบวก เช่น หักแชท 200)</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">ลำดับ</label>
                <input type="number" className="input" value={editing.sort_order ?? 100} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} />
              </div>
              <label className="flex items-end gap-2 pb-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={editing.active ?? true} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                ใช้งานอยู่
              </label>
            </div>
            <button onClick={save} disabled={upsert.isPending} className="btn-cta w-full justify-center flex items-center gap-2">
              <Save className="w-4 h-4" /> บันทึก
            </button>
            <button onClick={() => setEditing(null)} className="btn-secondary w-full justify-center flex items-center gap-2">
              <X className="w-4 h-4" /> ปิด
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
